import { useState } from "react";
import usePageState from "../../../hooks/usePageState";
import { ItemModel, SizeMappingModel } from "../../../../infrastructure/model/ProductItemModel";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import ActionHeader from "../../../components/ActionHeader";
import {
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import useModal from "../../../hooks/useModal";
import CreateItemDialog from "./CreateItemDialog";
import BaseTable from "../../../components/BaseTable";
import { useItemsContext } from "../../../context/AppItemsContext";
import { Image } from "antd";
import useConfirmAction from "../../../hooks/useConfirmAction";
import { useToppingContext } from "../../../context/AppToppingContext";
import Column from "../../../components/Column";
import {
  ITEM_CATEGORY_DATA,
  SIZE_MAPPING_OPTION,
} from "../../../../applications/constants/AppConst";
import { currencyFormat } from "../../../../applications/utils/NumberUtils";
import {
  deleteItemUseCase,
  getAllItemsUseCase,
} from "../../../../applications/useCase/ApiUseCase/ItemApiUseCase";

function ListItems() {
  const { items, setItems } = useItemsContext();
  const { toppings } = useToppingContext();
  const { isLoading, setLoading, showError, showSuccess } = usePageState();
  const [createModal, openCreateModal] = useModal();
  const [selectedItem, setSelectedItem] = useState<ItemModel>();
  const { dialog: confirmDeleteDialog, confirm: confirmDelete } = useConfirmAction({
    onConfirm(params) {
      handleDeleteItem();
    },
  });

  async function getAllItem() {
    const res = await getAllItemsUseCase({
      setLoading,
      showError,
    });
    if (res) {
      setItems(res);
    }
  }

  async function handleDeleteItem() {
    if (!selectedItem?.id) return;

    const res = await deleteItemUseCase({
      itemId: selectedItem.id,
      setLoading,
      showError,
      showSuccess,
    });
    if (res) {
      setItems(items.filter((e) => e.id !== selectedItem.id));
      setSelectedItem(undefined);
    }
  }

  async function handleOpenCreateDialog(updateItem?: ItemModel) {
    const result = await openCreateModal<ItemModel>((close) => {
      return <CreateItemDialog onClose={close} updateItem={updateItem} />;
    });
    if (result && result.id) {
      if (updateItem) {
        const indexUpdate = items.findIndex((e) => e.id === updateItem.id);
        setItems([...items.slice(0, indexUpdate), result, ...items.slice(indexUpdate + 1)]);
      } else {
        setItems([result, ...items]);
      }
      setSelectedItem(undefined);
    }
  }

  useAsyncEffect(async () => {
    await getAllItem();
  }, []);

  return (
    <div className="h-100 flex flex-column">
      <ActionHeader
        actions={[
          {
            icon: <ReloadOutlined />,
            isNormal: true,
            userClick: getAllItem,
            type: "primary",
          },
          {
            icon: <PlusCircleOutlined />,
            isNormal: true,
            userClick: handleOpenCreateDialog,
            type: "primary",
          },
          {
            icon: <EditOutlined />,
            isNormal: true,
            userClick: () => handleOpenCreateDialog(selectedItem),
            disabled: !selectedItem?.id,
            type: "primary",
          },
          {
            isNormal: true,
            userClick: confirmDelete,
            icon: <DeleteOutlined />,
            disabled: !selectedItem?.id,
            color: "danger",
            variant: "outlined",
          },
        ]}
      />
      <div className="flex-1">
        <BaseTable
          dataSource={items}
          loading={isLoading}
          columns={[
            {
              title: "ID",
              dataIndex: "id",
              key: "id",
              width: 100,
            },
            {
              title: "Ảnh sản phẩm",
              dataIndex: "image",
              key: "image",
              width: 60,
              render(value, record, index) {
                return (
                  <Image
                    src={value}
                    alt="No Image"
                    style={{
                      width: "40px",
                      height: "40px",
                    }}
                  />
                );
              },
            },
            {
              title: "Name",
              dataIndex: "name",
              key: "name",
            },
            {
              title: "Loại sản phẩm",
              dataIndex: "type",
              key: "type",
              render(value, record, index) {
                return ITEM_CATEGORY_DATA.find((c) => c.value === value)?.label;
              },
            },
            {
              title: "Mở bán",
              dataIndex: "open_sale",
              key: "open_sale",
              render(value, record, index) {
                return value ? "Có" : "Không";
              },
              width: 80,
            },
            {
              title: "Topping",
              dataIndex: "attached",
              key: "attached",
              render(value, record, index) {
                return toppings
                  .filter((e) => value?.includes(e.id))
                  ?.map((k) => k.name)
                  .join(", ");
              },
            },
            {
              title: "Size và giá tiền",
              dataIndex: "size_mapping",
              key: "size_mapping",
              render(value, record, index) {
                return (
                  <Column>
                    {value?.map((e: SizeMappingModel) => (
                      <div className="flex">
                        <span className="mr-2">
                          Size {SIZE_MAPPING_OPTION.find((o) => o.value === e.size)?.label}
                        </span>
                        <span>: {currencyFormat(e.price)}</span>
                      </div>
                    ))}
                  </Column>
                );
              },
            },
            {
              title: "Hot Sale",
              dataIndex: "hot_sale",
              key: "hot_sale",
              render(value, record, index) {
                return value ? "Bán chạy" : "";
              },
            },
          ]}
          rowKey={"id"}
          rowSelection={{
            type: "radio",
            selectedRowKeys: selectedItem?.id ? [selectedItem.id] : [],
            onSelect(record) {
              setSelectedItem(record);
            },
          }}
          pagination={{
            pageSize: 30,
          }}
        />
      </div>

      {createModal}
      {confirmDeleteDialog}
    </div>
  );
}

export default ListItems;
